import { Component, inject, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { CheckerComments, ValidationLevel } from '../../core/types';
import { displayString, commentTimeString } from '../../core/utils';
import { AUTH_CONTEXT, VALIDATION_SERVICE } from '../../core/tokens';

@Component({
	standalone: true,
	imports: [CommonModule, FormsModule],
	selector: 'wd-comments',
	templateUrl: './comments.component.html',
	styleUrls: ['./comments.component.css'],
})
export class CommentsComponent {
	@Input() name!: string;
	@Input() comments!: CheckerComments;
	@Input() inline = true;

	// Optional: Wenn innerhalb eines Modals, kann das Modal geschlossen werden
	private activeModal = inject(NgbActiveModal, { optional: true });

	// Services via InjectionTokens
	private auth = inject(AUTH_CONTEXT);
	public vs = inject(VALIDATION_SERVICE);

	ValRes = ValidationLevel;
	displayString = displayString;
	commentTimeString = commentTimeString;

	private isMyLastComment(idx: number) {
		return idx === this.comments!.comments!.length - 1
			&& this.comments!.comments[idx].name === this.auth.currentUser;
	}

	canComment(cmts: CheckerComments, idx: number) {
		if (!cmts || cmts.resolved) return false;
		return idx === undefined ? this.auth.commenting : this.isMyLastComment(idx);
	}

	canAddComment() {
		if (this.inline || !this.comments || this.comments.resolved) return false;
		const l = this.comments.comments.length;
		return l === 1
			|| this.comments.comments[l - 1].name !== this.auth.currentUser;
	}

	canDeleteComment(idx: number) {
		return idx > 0 && this.isMyLastComment(idx);
	}

	commentsClass(comments: CheckerComments): string {
		const rubber = this.auth.pen === 'rubber' ? 'rubber' : '';
		switch (comments?.type) {
			case ValidationLevel.danger: return `${rubber} danger`;
			case ValidationLevel.warning: return `${rubber} warning`;
			case ValidationLevel.ok: return `${rubber} message`;
			default: return '';
		}
	}

	onCommentsClicked(key: string) {
		if (this.auth.pen === 'rubber') this.vs.clearComments(key);
	}

	onAddComment() {
		this.comments.comments.push({ msg: '', time: Date.now(), name: this.auth.currentUser });
	}

	onCommentFocusOut(idx: number) {
		if (this.comments?.comments[idx].msg === '' && this.canDeleteComment(idx)) {
			this.deleteComment(idx);
		}
	}

	/** Wird aufgerufen wenn das "x" im Suchfeld geklickt wird */
	onCommentSearchClear(idx: number) {
		if (this.comments?.comments[idx]?.msg === '' && this.canDeleteComment(idx)) {
			this.deleteComment(idx);
		}
	}

	/** Löscht einen Kommentar und aktualisiert den ValidationState */
	private deleteComment(idx: number) {
		this.comments.comments.splice(idx, 1);
		// Wenn nur noch der initiale Kommentar übrig ist, Kommentar-Objekt komplett entfernen
		if (this.comments.comments.length <= 1) {
			this.vs.clearComments(this.name);
			// Wenn in Modal: Modal schließen ohne Wert, damit der Kommentar nicht wieder gesetzt wird
			this.activeModal?.close();
		} else {
			// Ansonsten nur State aktualisieren und speichern
			this.vs.resetComments(this.name, this.comments);
		}
	}
}
